import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Field, Form, Formik} from 'formik';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileTextIcon from '@mui/icons-material/Description';
import MapPinIcon from '@mui/icons-material/LocationOn';
import DollarSignIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import HashIcon from '@mui/icons-material/Tag';
import {useService} from '../../../../infrastructure/service/service';
import {BrandingTemplateDialog} from './BrandingTemplateDialog';
import {InvoiceDialogProps} from '../../../../entity/props/title/invoice-dialog-props';
import {BrandingTemplate} from '../../../../entity/model/branding-template';
import {Invoice} from '../../../../entity/model/invoice';
import {Section} from '../../../../entity/resource/section';

import '../../../../assets/css/title/invoice-dialog.css';
import moment from 'moment';


const InvoiceDialog = (props: InvoiceDialogProps): JSX.Element => {
  const {documentService, titleService, titleTemplateService, userService, invoiceRequestService, brandingTemplateService} = useService();

  const [isGenerating, setGenerating] = useState<boolean>(false);
  const [isBrandingOpen, setIsBrandingOpen] = useState<boolean>(false);
  const [brandingTemplates, setBrandingTemplates] = useState<BrandingTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);
  const [templatesLoaded, setTemplatesLoaded] = useState<boolean>(false);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (props.isOpen && !templatesLoaded) {
      const loadTemplates = async () => {
        setIsLoadingTemplates(true);
        const user = await userService.getCurrentUser();
        const result = await brandingTemplateService.loadTemplatesForUser(user?.organizationID ?? '');

        setBrandingTemplates(result || []);
        setTemplatesLoaded(true);
        setIsLoadingTemplates(false);
      };
      loadTemplates();
    }

    if (!props.isOpen) {
      setTemplatesLoaded(false);
      setIsLoadingTemplates(false);
    }
  }, [props.isOpen]);

  const reloadTemplates = async () => {
    setIsLoadingTemplates(true);
    const user = await userService.getCurrentUser();
    const result = await brandingTemplateService.loadTemplatesForUser(user?.organizationID ?? '');
    setBrandingTemplates(result || []);
    setIsLoadingTemplates(false);
  };

  const handleGenerateInvoice = async (values: Invoice): Promise<void> => {
    setGenerating(true);

    values.dueDate = moment(values.dueDate).utc().toISOString();
    const template = brandingTemplateService.getCurrentTemplate(values.selectedTemplate);

    const title = titleService.getTitle();
    const titleTemplate = titleTemplateService.getTitleTemplate();

    let sections: Section[] = [];
    let organizationId = '';
    let titleId = '';
    let templateId = '';

    if (title) {
      sections = title.sections ?? [];
      organizationId = title.createdByOrganizationID;
      titleId = title.id;
    } else if (titleTemplate) {
      sections = titleTemplate.sections ?? [];
      organizationId = titleTemplate.createdByOrganizationID ?? '';
      templateId = titleTemplate.id ?? '';
    }

    const uploadContext = invoiceRequestService.getUploadContext(sections, organizationId, titleId, templateId);
    if (!uploadContext) {
      setGenerating(false);
      return;
    }

    if (props.invoiceTemplate && title) {
      const sectionId = title.sections.find((section: any) => section.name === 'Invoices')?.id;
      if (sectionId) await documentService.deleteDocument(title.id, sectionId, props.invoiceTemplate.documentID, false, title.id);
    }

    const result = await invoiceRequestService.generateAndProcessInvoice(
      values,
      template,
      uploadContext,
      documentService.uploadFile.bind(documentService),
      titleService.getTitleById.bind(titleService),
      (organizationId: string, templateId: string, forceRefresh?: boolean) =>
        titleTemplateService.getTitleTemplateById(organizationId, templateId, forceRefresh || false),
      props.invoiceTemplate?._id
    );

    if (result) {
      if (props.onInvoiceCreated) props.onInvoiceCreated(values, result);

      titleService.setCache(false);

      await new Promise((resolve) => setTimeout(resolve, 500));
      props.onClose();
    }

    setGenerating(false);
  };

  const handleSaveBrandingTemplate = async (template: BrandingTemplate): Promise<void> => {
    const user = await userService.getCurrentUser();
    const result = await brandingTemplateService.saveTemplateForUser(template, user?.organizationID ?? '');

    if (result) {
      await reloadTemplates();
    }
  };

  const handleBrandingModalClose = useCallback(() => {
    setIsBrandingOpen(false);
    reloadTemplates();
  }, [reloadTemplates]);

  const handleValidate = (values: Invoice): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.invoiceNumber?.trim()) {
      errors.invoiceNumber = 'Invoice number is required';}
    else if (values.invoiceNumber.length < 3) {
      errors.invoiceNumber = 'Invoice number must be at least 3 characters';}

    if (!values.fee || values.fee.toString().trim() === '') {
      errors.fee = 'Service fee is required';
    } else {
      const feeValue = parseFloat(values.fee.toString());
      if (isNaN(feeValue)) {
        errors.fee = 'Service fee must be a valid number';}
      else if (feeValue < 0.01) {
        errors.fee = 'Service fee must be greater than 0';}
      else if (feeValue > 999999) {
        errors.fee = 'Service fee cannot exceed $999,999';}
    }

    if (values.receiptsCopyCosts && values.receiptsCopyCosts.toString().trim() !== '') {
      const receiptsValue = parseFloat(values.receiptsCopyCosts.toString());
      if (isNaN(receiptsValue)) {
        errors.receiptsCopyCosts = 'Receipts/Copy costs must be a valid number';}
      else if (receiptsValue < 0) {
        errors.receiptsCopyCosts = 'Receipts/Copy costs cannot be negative';}
      else if (receiptsValue > 999999) {
        errors.receiptsCopyCosts = 'Receipts/Copy costs cannot exceed $999,999';}
    }

    if (!values.dueDate?.trim()) {
      errors.dueDate = 'Due date is required';
    } else {
      const dueDate = new Date(values.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dueDate < today) {
        errors.dueDate = 'Due date cannot be in the past';
      }
    }

    if (values.notes && values.notes.length > 250) {
      errors.notes = 'Notes cannot exceed 250 characters';}

    if (!values.orderNumber?.trim()) {
      errors.orderNumber = 'Order number is required';}

    if (!values.propertyAddress?.trim()) {
      errors.propertyAddress = 'Property address is required';}

    if (!values.clientName?.trim()) {
      errors.clientName = 'Client name is required';}

    if (!values.selectedTemplate?.trim()) {
      errors.selectedTemplate = 'Please select a template';}

    return errors;
  };

  const getCurrentTemplate = useCallback((templateId: string): BrandingTemplate => {
    return brandingTemplateService.getCurrentTemplate(templateId);
  }, [brandingTemplateService]);

  return (
    <>
      <Dialog
        open={props.isOpen}
        onClose={props.onClose}
        maxWidth="lg"
        fullWidth
        className="invoice-creation-dialog zitles-dialog">
        <Formik
          initialValues={invoiceRequestService.getInitialValues(props.invoiceTemplate, props.orderData, brandingTemplates)}
          validate={handleValidate}
          onSubmit={handleGenerateInvoice}
          enableReinitialize={true}>
          {({values, errors, touched, handleChange, handleBlur, isValid}) => {
            const currentTemplate = getCurrentTemplate(values.selectedTemplate);
            return (
              <Form className="form-style">
                <DialogTitle className="zitles-dialog-title">
                  <Box className="dialog-title-box">
                    <span>Create Invoice</span>
                  </Box>
                  <IconButton id="btn-dialog-close" onClick={props.onClose}
                    className="dialog-close-button">
                    <CloseIcon className="dialog-icon"/>
                  </IconButton>
                </DialogTitle>
                <DialogContent dividers className="invoice-dialog-content">
                  <Grid container spacing={3} className="main-grid-container">
                    <Grid item xs={12} lg={6} className="form-grid-item">
                      <Box className="invoice-form-section form-section-box">
                        <Card className="zitles-card" elevation={0}>
                          <CardHeader
                            title="Invoice Template"
                            className="zitles-card-header"
                            action={
                              <Button
                                id="btn-configure-template"
                                variant="outlined"
                                size="small"
                                onClick={() => setIsBrandingOpen(true)}
                                className="zitles-button outlined"
                                disabled={isLoadingTemplates}>
                                Configure
                              </Button>
                            }/>
                          <CardContent className="zitles-card-content">
                            {
                              isLoadingTemplates
                                ? <Box display='flex' alignItems='center' justifyContent='center' minHeight='56px'>
                                    <CircularProgress size={20}/>
                                    <Typography variant="body2" className="loading-text">
                                      Loading templates...
                                    </Typography>
                                  </Box>
                                : <FormControl fullWidth
                                    error={touched.selectedTemplate && !!errors.selectedTemplate}
                                    size="small">
                                    <InputLabel>Select Template</InputLabel>
                                    <Select
                                      id="select-template"
                                      name="selectedTemplate"
                                      label="Select Template"
                                      className="zitles-select"
                                      value={values.selectedTemplate}
                                      onChange={handleChange}
                                      onBlur={handleBlur}>
                                      {
                                        brandingTemplates.map((template) =>
                                          <MenuItem key={template.id} value={template.id}>
                                            {template.name}
                                          </MenuItem>
                                        )
                                      }
                                    </Select>
                                    {
                                      touched.selectedTemplate && errors.selectedTemplate &&
                                        <FormHelperText>{errors.selectedTemplate}</FormHelperText>
                                    }
                                  </FormControl>
                            }
                          </CardContent>
                        </Card>
                        <Card className="zitles-card" elevation={0}>
                          <CardHeader
                            title="Order Information"
                            subheader="Auto-populated from order details"
                            className="zitles-card-header"/>
                          <CardContent className="zitles-card-content">
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  id="input-order-number"
                                  name="orderNumber"
                                  fullWidth
                                  size="small"
                                  label="Order Number"
                                  className="zitles-textfield"
                                  value={values.orderNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.orderNumber && !!errors.orderNumber}
                                  helperText={touched.orderNumber && errors.orderNumber}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <FileTextIcon fontSize="small"
                                          className="zitles-icon"/>
                                      </InputAdornment>
                                    ),
                                    className: 'monospace-font'
                                  }}/>
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  id="input-property-address"
                                  name="propertyAddress"
                                  fullWidth
                                  size="small"
                                  label="Property Address"
                                  className="zitles-textfield"
                                  value={values.propertyAddress}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.propertyAddress && !!errors.propertyAddress}
                                  helperText={touched.propertyAddress && errors.propertyAddress}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <MapPinIcon fontSize="small"
                                          className="zitles-icon"/>
                                      </InputAdornment>
                                    )
                                  }}/>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  id="input-parcel-number"
                                  name="parcelNumber"
                                  fullWidth
                                  size="small"
                                  label="Parcel Number"
                                  className="zitles-textfield"
                                  value={values.parcelNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.parcelNumber && !!errors.parcelNumber}
                                  helperText={touched.parcelNumber && errors.parcelNumber}
                                  InputProps={{
                                    className: 'monospace-font'
                                  }}/>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  id="input-client-name"
                                  name="clientName"
                                  fullWidth
                                  size="small"
                                  label="Client Name"
                                  className="zitles-textfield"
                                  value={values.clientName}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.clientName && !!errors.clientName}
                                  helperText={touched.clientName && errors.clientName}/>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                        <Card className="zitles-card" elevation={0}>
                          <CardHeader
                            title="Invoice Details"
                            subheader="Enter invoice number, fees, and due date"
                            className="zitles-card-header"/>
                          <CardContent className="zitles-card-content">
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  id="input-invoice-number"
                                  name="invoiceNumber"
                                  fullWidth
                                  size="small"
                                  label="Invoice Number *"
                                  required
                                  className="zitles-textfield"
                                  value={values.invoiceNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.invoiceNumber && !!errors.invoiceNumber}
                                  helperText={touched.invoiceNumber && errors.invoiceNumber}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <HashIcon fontSize="small"
                                          className="zitles-icon"/>
                                      </InputAdornment>
                                    ),
                                    className: 'monospace-font'
                                  }}/>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  id="input-service-fee"
                                  name="fee"
                                  fullWidth
                                  size="small"
                                  label="Service Fee *"
                                  type="number"
                                  required
                                  className="zitles-textfield"
                                  value={values.fee}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.fee && !!errors.fee}
                                  helperText={touched.fee && errors.fee}
                                  inputProps={{step: '0.01'}}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <DollarSignIcon fontSize="small"
                                          className="zitles-icon"/>
                                      </InputAdornment>
                                    )
                                  }}/>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  id="input-receipts-copy-costs"
                                  name="receiptsCopyCosts"
                                  fullWidth
                                  size="small"
                                  label="Receipts / Copy Costs"
                                  type="number"
                                  className="zitles-textfield"
                                  value={values.receiptsCopyCosts}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.receiptsCopyCosts && !!errors.receiptsCopyCosts}
                                  helperText={touched.receiptsCopyCosts && errors.receiptsCopyCosts}
                                  inputProps={{step: '0.01'}}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <DollarSignIcon fontSize="small"
                                          className="zitles-icon"/>
                                      </InputAdornment>
                                    )
                                  }}/>
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  id="input-due-date"
                                  name="dueDate"
                                  fullWidth
                                  size="small"
                                  label="Due Date *"
                                  type="date"
                                  required
                                  className="zitles-textfield"
                                  value={values.dueDate ? moment(values.dueDate).local().format('YYYY-MM-DD') : ''}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.dueDate && !!errors.dueDate}
                                  helperText={touched.dueDate && errors.dueDate}
                                  InputLabelProps={{shrink: true}}
                                  inputProps={{min: today}}/>
                              </Grid>
                              <Grid item xs={12}>
                                <Field name="notes">
                                  {({field, meta}: any) => (
                                    <TextField
                                      {...field}
                                      id="input-notes"
                                      fullWidth
                                      label="Additional Notes (Optional)"
                                      multiline
                                      minRows={4}
                                      maxRows={12}
                                      placeholder="Add any additional notes or descriptions for this invoice..."
                                      className="zitles-textfield multiline-notes"
                                      variant="outlined"
                                      helperText={`${field.value.length}/250 characters`}
                                      error={meta.touched && !!meta.error}
                                      inputProps={{
                                        maxLength: 250,
                                        className: 'multiline-input-style'
                                      }}
                                      InputProps={{
                                        className: 'multiline-container-style'
                                      }}
                                      sx={{
                                        '& .MuiFormHelperText-root': {
                                          fontSize: '12px',
                                          color: field.value.length > 200 ? '#ff9800' : field.value.length === 250 ? '#f44336' : '#666'
                                        }
                                      }}/>
                                  )}
                                </Field>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Box>
                    </Grid>
                    <Grid item xs={12} lg={6} className="preview-grid-item">
                      <Card className="zitles-card invoice-preview-card" elevation={0}>
                        <CardHeader
                          title="Invoice Preview"
                          subheader="Live preview with your branding"
                          className="zitles-card-header invoice-preview-card-header"/>
                        <CardContent className="zitles-card-content invoice-preview-content">
                          <Box className="invoice-preview-content-box">
                            <Box className="invoice-preview-document">
                              <Box className="preview-header">
                                <Box>
                                  {
                                    currentTemplate.logo &&
                                      <img src={currentTemplate.logo}
                                           alt="Logo"
                                           className="company-logo"/>
                                  }
                                  <Typography variant="h6" className="company-name">
                                    {currentTemplate.companyName}
                                  </Typography>
                                  <Typography variant="body2" className="company-address">
                                    {currentTemplate.companyAddress}
                                  </Typography>
                                  <Box className="company-contact">
                                    {
                                      currentTemplate.phone &&
                                        <Typography variant="caption" className="company-contact-item">Phone: {currentTemplate.phone}</Typography>
                                    }
                                    {
                                      currentTemplate.email &&
                                        <Typography variant="caption" className="company-contact-item">Email: {currentTemplate.email}</Typography>
                                    }
                                    {
                                      currentTemplate.website &&
                                        <Typography variant="caption" className="company-contact-item">Website: {currentTemplate.website}</Typography>
                                    }
                                  </Box>
                                </Box>
                                <Box className="invoice-meta">
                                  <Typography variant="caption" className="invoice-meta-item">Invoice #: {values.invoiceNumber}</Typography>
                                  <Typography variant="caption" className="invoice-meta-item">Date: {new Date().toLocaleDateString()}</Typography>
                                  <Typography variant="caption" className="invoice-meta-item">Due:{values.dueDate ? new Date(values.dueDate).toLocaleDateString() : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box className="bill-to-section">
                                <Typography variant="subtitle2" className="bill-to-title">Bill To:</Typography>
                                <Typography variant="body2" className="bill-to-client">{values.clientName}</Typography>
                              </Box>
                              <Box className="services-section">
                                <Box className="services-header">
                                  <Typography
                                    variant="subtitle2">Description</Typography>
                                  <Typography variant="subtitle2">Amount</Typography>
                                </Box>
                                <Box className="service-row">
                                  <Box>
                                    <Typography variant="body2" className="service-description">Title Search Services - Order {values.orderNumber}</Typography>
                                    <Typography variant="caption"
                                      color="text.secondary"
                                      className="service-address">
                                      {values.propertyAddress}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" className="service-amount">${Number.parseFloat(values.fee || '0').toFixed(2)}</Typography>
                                </Box>
                                {
                                  values.receiptsCopyCosts && Number.parseFloat(values.receiptsCopyCosts) > 0 &&
                                    <Box className="service-row">
                                      <Typography variant="body2" className="service-description">Receipts / Copy Costs</Typography>
                                      <Typography variant="body2" className="service-amount">${Number.parseFloat(values.receiptsCopyCosts || '0').toFixed(2)}</Typography>
                                    </Box>
                                }
                                <Box className="total-row">
                                  <Typography variant="subtitle2" className="total-label">Total:</Typography>
                                  <Typography variant="subtitle2" className="total-amount">${(Number.parseFloat(values.fee || '0') + Number.parseFloat(values.receiptsCopyCosts || '0')).toFixed(2)}</Typography>
                                </Box>
                              </Box>
                              {
                                values.notes &&
                                  <Box className="notes-section">
                                    <Typography variant="subtitle2" className="notes-title">Notes:</Typography>
                                    <Typography variant="body2" className="notes-content">{values.notes}</Typography>
                                  </Box>
                              }
                              <Box className="footer-section">
                                <Typography variant="caption" className="footer-text">
                                  {currentTemplate.footerText}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </DialogContent>
                <Divider sx={{my: 0, mx: 0}}/>
                <DialogActions className="action-btn-container">
                  <Button
                    id="btn-cancel"
                    onClick={props.onClose}
                    className="zitles-button cancel">
                    Cancel
                  </Button>
                  <Button
                    id="btn-generate-pdf"
                    type="submit"
                    disabled={!isValid || isGenerating || isLoadingTemplates}
                    className={`zitles-button outlined submit ${(!isValid || isGenerating || isLoadingTemplates) ? 'disable' : ''}`}
                    startIcon={isGenerating ? <CircularProgress size={16} color="inherit"/> : <DownloadIcon/>}>
                    {
                      isGenerating
                        ? 'Adding to Invoices...'
                        : 'Generate PDF'
                    }
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
      <BrandingTemplateDialog
        isOpen={isBrandingOpen}
        onClose={handleBrandingModalClose}
        onSave={handleSaveBrandingTemplate}
        templates={brandingTemplates}/>
    </>
  );
}

export default observer(InvoiceDialog);
